import {
    createFileRoute,
    Link,
    useNavigate,
    useParams,
} from "@tanstack/react-router";
import { GenerateRxResponse, ListRx } from "@/types/rx";
import { sortArrayByKey } from "@/lib/utils/sorting";
import {
    Button,
    ButtonGroup,
    Skeleton,
    Divider,
    Card,
    CardHeader,
    Chip,
    useDisclosure,
    Modal,
    ModalContent,
    ModalBody,
} from "@heroui/react";
import { useEffect, useState, useCallback, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRotate, faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import dayjs from "dayjs";
import useLayout from "@/lib/layout/useLayout";
import useIsMobile from "@/lib/utils/useIsMobile";
import useRequest from "@/lib/request/useRequest";
import GenerateRx from "@/components/rx/GenerateRx";
import useFieldDashboard from "@/lib/fieldDashboard/useFieldDashboard";
import useAuth from "@/lib/auth/useAuth";
import { OnDemandRxType, SimulationWeatherYear } from "@/types/enums";
import useFeatureFlag from "@/lib/featureFlags/useFeatureFlags";
import { useTranslation } from "react-i18next";
import { Resource } from "@/types/user";

export const Route = createFileRoute("/dashboard/fields/$fieldId/rx/")({
    component: RouteComponent,
});

function RxListCard({ rx, onDelete }: { rx: ListRx; onDelete?: () => void }) {
    const { t } = useTranslation();
    const { useMutateRequest } = useRequest();
    const deleteRxRequest = useMutateRequest({
        url: `prescriptions/${rx.id}/`,
        method: "delete",
        errorMessage: t("error.failed_to_delete_rx", { ns: "messages" }),
        successMessage: t("success.rx_deleted", { ns: "messages" }),
        onSuccess: () => {
            if (onDelete) {
                onDelete();
            }
        },
    });
    const deleteRx = useCallback(() => {
        deleteRxRequest.mutate({});
    }, [deleteRxRequest]);

    return (
        <Card className="w-full hover:bg-gray-200">
            <CardHeader className="flex flex-row justify-between py-2 px-4">
                <div className="flex flex-col">
                    <Button
                        isIconOnly
                        color="danger"
                        size="sm"
                        onPress={deleteRx}
                        aria-label="Delete Rx"
                    >
                        <FontAwesomeIcon icon={faTrash} />
                    </Button>
                </div>
                <Link
                    to="/dashboard/fields/$fieldId/rx/$rxId"
                    params={{
                        fieldId: rx.field.id.toString(),
                        rxId: rx.id.toString(),
                    }}
                    className="flex flex-row justify-between w-full items-center pl-3"
                >
                    <div className="flex flex-col">
                        <p className="text-md font-semibold">{rx.name}</p>
                        <p className="text-sm text-gray-500">
                            {rx.fertilizer_product}
                        </p>
                        <p className="text-sm text-gray-500">
                            {dayjs(rx.created_date).format("LL")}
                        </p>
                    </div>
                    <div className="flex flex-col">
                        <Chip
                            classNames={{
                                base: "bg-brandNavy-500 text-white",
                            }}
                            size="sm"
                            radius="sm"
                        >
                            {rx.rx_type}
                        </Chip>
                        {rx.plot_ids && rx.plot_ids.length > 0 ? (
                            <p className="text-sm text-gray-500">
                                {rx.plot_ids.length} plots
                            </p>
                        ) : null}
                    </div>
                </Link>
            </CardHeader>
        </Card>
    );
}

function RouteComponent() {
    const { t } = useTranslation();
    const { userIsSentinel, isAtLeastDetail } = useAuth();
    const navigate = useNavigate();
    const { useGetRequest, useMutateRequest } = useRequest();
    const { setLayoutState } = useLayout();
    const { isMobile } = useIsMobile();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const { fieldQuery } = useFieldDashboard() ?? {};
    const { sidedressOnDemandRx } = useFeatureFlag();
    useEffect(() => {
        setLayoutState("standard");
    }, [isMobile, setLayoutState]);

    const { fieldId } = useParams({
        strict: false,
    });
    const [rxType, setRxType] = useState<"My Rxs" | "Recommendations">(
        "My Rxs",
    );

    const recommendations = useGetRequest<ListRx[]>({
        url: `fields/${fieldId}/prescriptions/?recommended=true`,
    });
    const myRxs = useGetRequest<ListRx[]>({
        url: `fields/${fieldId}/prescriptions/?recommended=false`,
    });
    const rxList = useMemo(() => {
        if (rxType === "My Rxs") {
            return myRxs.data || [];
        } else if (rxType === "Recommendations") {
            return recommendations.data || [];
        }
        return [];
    }, [rxType, myRxs, recommendations]);
    const handleRxDelete = useCallback(() => {
        if (rxType === "My Rxs") {
            myRxs.refetch();
        } else {
            recommendations.refetch();
        }
    }, [rxType, myRxs, recommendations]);

    const generateRxRequest = useMutateRequest<GenerateRxResponse>({
        url: `prescriptions/on_demand/`,
        method: "post",
        errorMessage: t("error.failed_to_generate_rx", {
            ns: "messages",
        }),
        successMessage: t("success.generated_rx", {
            ns: "messages",
        }),
        onSuccess: (data) => {
            navigate({
                to: `/dashboard/fields/${fieldId}/rx/${data.prescription_id}`,
            });
            onClose();
        },
    });

    const handleMakeRx = useCallback(() => {
        if (userIsSentinel || sidedressOnDemandRx) {
            onOpen();
        } else {
            generateRxRequest.mutate({
                data: {
                    field: Number(fieldId),
                    on_demand_type: OnDemandRxType.ShortTerm,
                    imagery: fieldQuery?.data?.recommendation?.imagery_id,
                    technique: "Fertigation",
                    weather_year: SimulationWeatherYear.TEN_YEAR_MEDIAN,
                },
            });
        }
    }, [
        fieldId,
        fieldQuery?.data?.recommendation?.imagery_id,
        generateRxRequest,
        onOpen,
        userIsSentinel,
        sidedressOnDemandRx,
    ]);

    return (
        <>
            <div className="grid gap-2 grid-flow-row w-full  md:flex md:flex-row md:justify-between p-3">
                <ButtonGroup className="w-full md:w-auto" fullWidth={isMobile}>
                    <Button
                        color="primary"
                        size="sm"
                        variant={rxType === "My Rxs" ? "solid" : "bordered"}
                        onPress={() => setRxType("My Rxs")}
                    >
                        {t("my_rxs", { ns: "rx" })}
                    </Button>
                    <Button
                        color="primary"
                        size="sm"
                        variant={
                            rxType === "Recommendations" ? "solid" : "bordered"
                        }
                        onPress={() => setRxType("Recommendations")}
                    >
                        {t("recommendations", { ns: "rx" })}
                    </Button>
                </ButtonGroup>
                <div className="flex gap-1 w-full md:justify-end ">
                    {(userIsSentinel ||
                        sidedressOnDemandRx ||
                        (!fieldQuery?.data?.recommendation?.gap &&
                            fieldQuery?.data?.recommendation
                                ?.insights_active) ||
                        isAtLeastDetail(Resource.GENERATE_RX)) && (
                            <Button
                                color="secondary"
                                size="sm"
                                variant="solid"
                                className="w-full md:w-auto"
                                onPress={handleMakeRx}
                                isLoading={generateRxRequest.isPending}
                            >
                                <FontAwesomeIcon icon={faRotate} />
                                {t("generate_rx", { ns: "rx" })}
                            </Button>
                        )}
                    <Button
                        color="secondary"
                        size="sm"
                        variant="solid"
                        as={Link}
                        to="/dashboard/fields/$fieldId/rx/new"
                        className="w-full md:w-auto"
                    >
                        <FontAwesomeIcon icon={faPlus} />
                        {t("make_rx", { ns: "rx" })}
                    </Button>
                </div>
            </div>
            <Divider className="my-2" />
            <>
                {recommendations.isFetching || myRxs.isFetching ? (
                    <div className="h-full w-full p-2">
                        <Skeleton className="h-full w-full rounded-lg " />
                    </div>
                ) : (
                    <div className="flex flex-col gap-2 p-2">
                        {rxList?.length > 0 ? (
                            sortArrayByKey(rxList, "created_date").map((rx) => (
                                <RxListCard
                                    key={rx.id}
                                    rx={rx}
                                    onDelete={handleRxDelete}
                                />
                            ))
                        ) : (
                            <p className="text-gray-500 text-sm">
                                {t("no_rxs_found", { ns: "rx" })}
                            </p>
                        )}
                    </div>
                )}
            </>
            <Button className="fixed bottom-4 right-4">Load More</Button>
            <Modal
                isOpen={isOpen}
                onClose={onClose}
                size="2xl"
                scrollBehavior="inside"
            >
                <ModalContent>
                    <ModalBody>
                        <GenerateRx onClose={onClose} />
                    </ModalBody>
                </ModalContent>
            </Modal>
        </>
    );
}
